import { JSX, useMemo } from "react"
import { BarChart } from '@mui/x-charts/BarChart';
import {
    Box,
    Grid,
    IconButton,
    Skeleton,
    Tooltip,
    Typography
} from '@mui/material';
import { useTokenHistoryGraphData } from "./hooks/token-history-graph.hooks";
import { TokenHistoryBucket } from "../../../types/dashboard";
import './styles/token-history-graphs.scss';
import ReplayIcon from '@mui/icons-material/Replay';
import {
    GaugeContainer,
    GaugeValueArc,
    GaugeReferenceArc,
    gaugeClasses,
} from '@mui/x-charts/Gauge';
import { DashboardCard, DashboardCardError } from "./DashboardCard";
import { numeric, eyebrow, series, formatTokens, formatCompact, formatUsd, formatGbp, moneyPlaces } from "./styles/dashboard-tokens";
import { useSharedTokenCost } from "./context/TokenCostContext";

export const TokenHistoryGraph: () => JSX.Element = (): JSX.Element => {
    const { data, loading, error, reloadData } = useTokenHistoryGraphData();
    // The all-time line answers "what have we spent" as well as "how many tokens", and the two
    // come from different endpoints. Shared rather than fetched again here, so both cards on this
    // tab always show the same figure from the same moment.
    const { data: cost } = useSharedTokenCost();

    // Sits above the early returns because hooks cannot be called conditionally — and it only
    // recomputes when the payload actually changes, rather than on every unrelated render.
    const graphData: TokenHistoryBucket[] = useMemo((): TokenHistoryBucket[] => {
        const points: TokenHistoryBucket[] = [];
        // `data` is null until the first response lands, and this memo runs before the body
        // decides what to draw, so the optional chain is load-bearing rather than defensive noise.
        if (data?.history) {
            const yesterday: TokenHistoryBucket[] = data.history[data.keys.yesterday] ?? [];
            for (let i = 0; i < yesterday.length; i++) {
                points.push(yesterday[i]);
            }

            // Both days are walked oldest-first so the x-axis reads in one direction. The API
            // aggregates each day down to a single bucket, so this currently yields two bars.
            const today: TokenHistoryBucket[] = data.history[data.keys.today] ?? [];
            for (let i = 0; i < today.length; i++) {
                points.push(today[i]);
            }
        }
        return points;
    }, [data]);

    function handleRefresh() {
        reloadData();
    }

    const refreshAction: JSX.Element = (
        <Tooltip title="Reload token history">
            <IconButton aria-label="Reload token history" size="small" onClick={ handleRefresh }>
                <ReplayIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    const used: number = data?.totalTokens ?? 0;
    const budget: number = data?.totalTokenCounter ?? 0;
    const pct: number = budget > 0 ? Math.round((used / budget) * 100) : 0;

    // The figure the card exists to report, stated plainly above the chart rather than left for
    // the reader to infer from an arc.
    const budgetMeta: JSX.Element = (
        <Box sx={ { display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' } }>
            <Typography component="span" sx={ { ...numeric, fontSize: 26, fontWeight: 600, lineHeight: 1 } }>
                { formatTokens(used) }
            </Typography>
            <Typography component="span" variant="body2" color="text.secondary">
                { "of " + formatTokens(budget) + " · " + pct + "%" }
            </Typography>
        </Box>
    );

    // The card shells stay mounted through loading and failure, so the grid never reflows and the
    // title and reload button do not disappear and come back.
    return (
        <Grid container spacing={2}>
            <Grid size={ { xs: 12, md: 7 } }>
                <DashboardCard
                    eyebrow="Usage"
                    title="Request & response tokens"
                    actions={ refreshAction }
                    busy={ loading }
                >
                    { loading &&
                        <Skeleton variant="rounded" animation="wave" height={ 290 } />
                    }
                    { (!loading && error !== null) &&
                        <DashboardCardError
                            message={ "Token history unavailable — " + error }
                            onRetry={ handleRefresh }
                        />
                    }
                    { (!loading && error === null) &&
                        <Box className="token-history-graph">
                            <BarChart
                                dataset={ graphData }
                                series={[
                                    { dataKey: 'request', label: 'Request tokens', color: series.request, yAxisId: 'tokens' },
                                    { dataKey: 'response', label: 'Response tokens', color: series.response, yAxisId: 'tokens' },
                                    // Request *count* is in the tens while token counts are in
                                    // the hundreds of thousands. Sharing one axis flattened this
                                    // series to an invisible sliver, so it gets its own.
                                    { dataKey: 'requests', label: 'Requests', color: series.runs, yAxisId: 'runs' }
                                ]}
                                height={290}
                                grid={ { horizontal: true } }
                                xAxis={[{ dataKey: 'date' }]}
                                // Token counts run to six digits, so the default formatter was
                                // rendering "600000" and the axis clipped it to "600…".
                                yAxis={[
                                    { id: 'tokens', valueFormatter: (value: number) => formatCompact(value) },
                                    { id: 'runs', position: 'right', valueFormatter: (value: number) => formatCompact(value) },
                                ]}
                                borderRadius={4}
                            />
                        </Box>
                    }
                </DashboardCard>
            </Grid>
            <Grid size={ { xs: 12, md: 5 } }>
                <DashboardCard
                    eyebrow="Budget"
                    title="Token usage today"
                    actions={ refreshAction }
                    meta={ !loading && error === null ? budgetMeta : undefined }
                    busy={ loading }
                >
                    { loading &&
                        <>
                            <Skeleton variant="rounded" animation="wave" height={ 160 } />
                            <Skeleton variant="text" animation="wave" sx={ { mt: 2 } } />
                        </>
                    }
                    { (!loading && error !== null) &&
                        <DashboardCardError
                            message={ "Token history unavailable — " + error }
                            onRetry={ handleRefresh }
                        />
                    }
                    { (!loading && error === null) &&
                        <>
                            <Box className="token-history-graph column">
                                { /* The needle is gone: a thick value arc plus the exact figure
                                     above already say what it said, and the red pointer read as
                                     an alert on a metric that is not one. */ }
                                <GaugeContainer
                                    width={260}
                                    height={140}
                                    startAngle={-90}
                                    endAngle={90}
                                    innerRadius="70%"
                                    outerRadius="100%"
                                    cornerRadius={4}
                                    value={ used }
                                    valueMax={ budget }
                                    sx={ (theme) => ({
                                        [`& .${gaugeClasses.valueArc}`]: { fill: series.request },
                                        [`& .${gaugeClasses.referenceArc}`]: {
                                            fill: theme.palette.action.hover,
                                        },
                                    }) }
                                >
                                    <GaugeReferenceArc />
                                    <GaugeValueArc />
                                </GaugeContainer>
                            </Box>
                            <Box
                                className="total-tokens-ever"
                                sx={ {
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    pt: 1.5,
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                } }
                            >
                                <Typography component="span" sx={ eyebrow }>{ "All time" }</Typography>
                                <Box sx={ { display: 'flex', alignItems: 'baseline', gap: 1 } }>
                                    <Typography component="span" sx={ { ...numeric, fontSize: 15, fontWeight: 600 } }>
                                        { formatTokens(data?.foreverTotalTokenCount ?? 0) }
                                    </Typography>
                                    { /* Absent rather than zero while the cost is still loading or
                                         has failed. A confident "US$0.00" beside a real token count
                                         is the one wrong answer this line could give. */ }
                                    { cost !== null &&
                                        <>
                                            <Typography component="span" sx={ { ...numeric, fontSize: 13, fontWeight: 600 } }>
                                                { formatUsd(cost.allTime.usd, moneyPlaces(cost.allTime.usd)) }
                                            </Typography>
                                            <Typography
                                                component="span"
                                                sx={ { ...numeric, fontSize: 12 } }
                                                color="text.secondary"
                                            >
                                                { formatGbp(cost.allTime.gbp, moneyPlaces(cost.allTime.usd)) }
                                            </Typography>
                                        </>
                                    }
                                </Box>
                            </Box>
                        </>
                    }
                </DashboardCard>
            </Grid>
        </Grid>
    );
}
